import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  TableBody,
  TableCell,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Sort as SortIcon,
  Star as StarIcon,
  Code as CodeIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import SearchableDropdown from '../../../../shared/ui/forms/SearchableDropdown';
import TableHeaderField from '../../../../shared/ui/table/TableHeaderField';
import { Attribute } from '../../../../shared/types';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { BodyText } from '../../../../shared/ui/typography';
import {
  mappingSectionActionTooltips,
  mappingTableTooltips,
  mappingWorkspaceTooltips,
} from '../../../../shared/config/mappingHelpCatalog';
import { renderHelpContent } from '../../../../shared/ui/common/RichTooltipContent';
import { releaseFocusBeforeOverlay } from '../../../../shared/lib/utils/accessibilityUtils';
import {
  CompactAddAction,
  MappingEditorHeader,
  MappingRow,
  ScopeEditorToolbar,
  ScopeEditorTableVariant,
  getScopeEditorDropdownSx,
  getScopeEditorActionButtonSx,
  getScopeEditorSelectSx,
  getScopeEditorTextFieldSx,
} from '../../../../shared/ui/mapping/ScopeEditorPrimitives';
import {
  borderRadius, getSemanticSurfaceTokens, layoutGap,
} from '../../../../theme/theme';
import { mappingDensity } from '../../../../shared/ui/mapping';
import { MappingStatusChip } from '../../../../shared/ui/mapping';
import {
  getColumnWidths,
  getVisibleAttributes,
  normalizeAttributeToken,
  type MappingTableDensity,
  type MappingTableFilters,
  type MappingSortConfig,
} from '../../lib/mappingTableModel';
import PythonScriptEditorDialog from './PythonScriptEditorDialog';
interface MappingTableProps {
  selectedObjectType: string;
  currentAttributes: Attribute[];
  revitParameterOptions: { value: string; label: string; group: string }[];
  onAttributeChange: (order: number, field: keyof Attribute, value: string) => void;
  onRemoveAttribute?: (order: number) => void;
  onAddAttribute?: () => void;
  getAttributeAriaProps?: (attribute: Attribute) => {
    id: string;
    'aria-label': string;
  };
  /** Compact mode for laptops */
  dense?: boolean;
  /** Required attribute names for inline indicators */
  requiredAttributeNames?: string[];
  highlightedAttributeNames?: string[];
  tableControlsRef?: React.Ref<HTMLTableRowElement>;
  pcfColumnRef?: React.RefObject<HTMLDivElement | null>;
  revitColumnRef?: React.RefObject<HTMLDivElement | null>;
  typeColumnRef?: React.RefObject<HTMLDivElement | null>;
  formulaColumnRef?: React.RefObject<HTMLDivElement | null>;
  rowActionsRef?: React.RefObject<HTMLDivElement | null>;
}

const MappingTable: React.FC<MappingTableProps> = ({
  selectedObjectType,
  currentAttributes,
  revitParameterOptions,
  onAttributeChange,
  onRemoveAttribute,
  onAddAttribute,
  getAttributeAriaProps,
  dense = false,
  requiredAttributeNames = [],
  highlightedAttributeNames = [],
  tableControlsRef,
  pcfColumnRef,
  revitColumnRef,
  typeColumnRef,
  formulaColumnRef,
  rowActionsRef,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('md'));
  const tableDensity: MappingTableDensity = isNarrowScreen
    ? 'narrow'
    : isCompactScreen
      ? 'compact'
      : 'regular';
  const columnWidths = useMemo(() => getColumnWidths(tableDensity), [tableDensity]);
  const showOrderColumn = !isNarrowScreen;
  const compactInputHeight = isNarrowScreen
    ? mappingDensity.tableInputNarrowHeight
    : dense
      ? mappingDensity.tableInputCompactHeight
      : mappingDensity.tableInputHeight;
  const compactRowHeight = isNarrowScreen
    ? mappingDensity.tableRowNarrowHeight
    : dense
      ? mappingDensity.tableRowCompactHeight
      : mappingDensity.tableRowHeight;

  const [sortConfig, setSortConfig] = useState<MappingSortConfig | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [filters, setFilters] = useState<MappingTableFilters>({
    pcfAttributeName: '',
    revitParam: '',
    attributeType: '',
    fx: '',
  });
  const [isScriptEditorOpen, setIsScriptEditorOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<Attribute | null>(null);

  const normalizedHighlightedAttributes = useMemo(
    () => highlightedAttributeNames.map(normalizeAttributeToken).filter(Boolean),
    [highlightedAttributeNames]
  );
  const highlightKeyRef = useRef('');
  const [workspaceLens, setWorkspaceLens] = useState<
    'all' | 'focused'
  >('all');

  const isFormulaMapping = useCallback((attribute: Attribute) => {
    return attribute.attributeType.trim().toLowerCase() === 'formula';
  }, []);

  const isRequired = useCallback(
    (attr: Attribute) => {
      return requiredAttributeNames.includes(attr.pcfAttributeName);
    },
    [requiredAttributeNames]
  );

  const isMapped = useCallback(
    (attr: Attribute) => {
      return (
        (attr.revitParam && attr.revitParam.trim() !== '') ||
        (isFormulaMapping(attr) && attr.fx.trim() !== '')
      );
    },
    [isFormulaMapping]
  );

  useEffect(() => {
    const nextHighlightKey = normalizedHighlightedAttributes.join('|');

    if (nextHighlightKey && nextHighlightKey !== highlightKeyRef.current) {
      setWorkspaceLens('focused');
    }

    if (!nextHighlightKey && highlightKeyRef.current) {
      setWorkspaceLens((current) => (current === 'focused' ? 'all' : current));
    }

    highlightKeyRef.current = nextHighlightKey;
  }, [normalizedHighlightedAttributes]);

  const handleSort = useCallback((key: keyof Attribute) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  const sortedAndFilteredAttributes = useMemo(
    () =>
      getVisibleAttributes(
        currentAttributes,
        filters,
        sortConfig,
        workspaceLens,
        highlightedAttributeNames
      ),
    [currentAttributes, filters, sortConfig, workspaceLens, highlightedAttributeNames]
  );

  const handleSelectRow = useCallback((order: number) => {
    setSelectedRows((prev) =>
      prev.includes(order) ? prev.filter((id) => id !== order) : [...prev, order]
    );
  }, []);

  const handleSelectAllRows = useCallback(() => {
    if (selectedRows.length === sortedAndFilteredAttributes.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(sortedAndFilteredAttributes.map((attr) => attr.order));
    }
  }, [selectedRows.length, sortedAndFilteredAttributes]);

  const handleFilterChange = useCallback((field: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      pcfAttributeName: '',
      revitParam: '',
      attributeType: '',
      fx: '',
    });
    setWorkspaceLens('all');
  }, []);

  const hasActiveFilters = Object.values(filters).some((value) => value !== '');
  const hasActiveWorkspaceLens = workspaceLens !== 'all';
  const hasAnyActiveFilter = hasActiveFilters || hasActiveWorkspaceLens;
  const tableColumnCount = 5 + (showOrderColumn ? 1 : 0) + (onRemoveAttribute ? 1 : 0);

  const openScriptEditor = useCallback((attribute: Attribute) => {
    setEditingAttribute(attribute);
    setIsScriptEditorOpen(true);
  }, []);

  const closeScriptEditor = useCallback(() => {
    setIsScriptEditorOpen(false);
    setEditingAttribute(null);
  }, []);

  const saveScript = useCallback((script: string) => {
    if (editingAttribute) {
      onAttributeChange(editingAttribute.order, 'fx', script);
    }
    closeScriptEditor();
  }, [editingAttribute, onAttributeChange, closeScriptEditor]);

  const handleRemoveRow = useCallback(
    (attribute: Attribute) => {
      onRemoveAttribute?.(attribute.order);
      setSelectedRows((prev) => prev.filter((order) => order !== attribute.order));
    },
    [onRemoveAttribute]
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        overflow: 'hidden',
      }}
    >
      {normalizedHighlightedAttributes.length > 0 && (
        <ScopeEditorToolbar
          primary={
            <ActionHelpTooltip help={mappingWorkspaceTooltips.focusedLens} placement="bottom-start">
              <Button
                size="small"
                variant={workspaceLens === 'focused' ? 'contained' : 'outlined'}
                onClick={() =>
                  setWorkspaceLens((current) => (current === 'focused' ? 'all' : 'focused'))
                }
                aria-pressed={workspaceLens === 'focused'}
                sx={{
                  minHeight: compactInputHeight,
                  px: 0.5,
                  fontSize: '0.6875rem',
                  borderRadius: `${borderRadius.md}px`,
                }}
              >
                Focused
              </Button>
            </ActionHelpTooltip>
          }
        />
      )}
      <ScopeEditorTableVariant
        ariaLabel="PCF attribute mappings for the selected object type"
        minWidth={isCompactScreen ? '100%' : onRemoveAttribute ? 516 : 480}
        rowHeight={compactRowHeight}
      >
        <MappingEditorHeader
          rowRef={tableControlsRef}
          columns={[
            {
              key: 'checkbox',
              width: columnWidths.checkbox,
              minWidth: columnWidths.checkbox,
              align: 'center',
              content: (
                <ActionHelpTooltip help={mappingTableTooltips.selectVisibleRows} placement="bottom-start">
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={
                      sortedAndFilteredAttributes.length > 0 &&
                      selectedRows.length === sortedAndFilteredAttributes.length
                    }
                    onChange={handleSelectAllRows}
                    aria-label="Select all visible PCF mappings"
                    inputProps={{ 'aria-label': 'Select all visible PCF mappings' }}
                    sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: '0.875rem' } }}
                  />
                </ActionHelpTooltip>
              ),
            },
            ...(showOrderColumn
              ? [
                  {
                    key: 'order',
                    width: columnWidths.order,
                    minWidth: columnWidths.order,
                    align: 'center' as const,
                    content: (
                      <ActionHelpTooltip help={mappingTableTooltips.rowOrder} placement="bottom-start">
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                          spacing={layoutGap.micro}
                          onClick={() => handleSort('order')}
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          <span>#</span>
                          {sortConfig?.key === 'order' && (
                            <SortIcon
                              sx={{
                                fontSize: '0.75rem',
                                transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'none',
                              }}
                            />
                          )}
                        </Stack>
                      </ActionHelpTooltip>
                    ),
                  },
                ]
              : []),
            {
              key: 'pcfAttribute',
              width: columnWidths.pcfAttribute,
              content: (
                <Box ref={pcfColumnRef}>
                  <TableHeaderField
                    label={isNarrowScreen ? 'PCF target' : 'PCF attribute target'}
                    value={filters.pcfAttributeName}
                    onChange={(value) => handleFilterChange('pcfAttributeName', value)}
                    onSort={() => handleSort('pcfAttributeName')}
                    sortDirection={sortConfig?.key === 'pcfAttributeName' ? sortConfig.direction : null}
                    ariaLabel="Filter PCF attribute targets"
                    help={isNarrowScreen ? undefined : mappingTableTooltips.pcfAttributeColumn}
                    inputHeight={compactInputHeight}
                  />
                </Box>
              ),
            },
            {
              key: 'revitParam',
              width: columnWidths.revitParam,
              content: (
                <Box ref={revitColumnRef}>
                  <TableHeaderField
                    label={isNarrowScreen ? 'Revit source' : 'Revit source parameter'}
                    value={filters.revitParam}
                    onChange={(value) => handleFilterChange('revitParam', value)}
                    onSort={() => handleSort('revitParam')}
                    sortDirection={sortConfig?.key === 'revitParam' ? sortConfig.direction : null}
                    ariaLabel="Filter Revit source parameters"
                    help={isNarrowScreen ? undefined : mappingTableTooltips.revitParameterColumn}
                    inputHeight={compactInputHeight}
                  />
                </Box>
              ),
            },
            {
              key: 'type',
              width: columnWidths.type,
              content: (
                <Box ref={typeColumnRef}>
                  <TableHeaderField
                    label="Source type"
                    value={filters.attributeType}
                    onChange={(value) => handleFilterChange('attributeType', value)}
                    selectOptions={[
                      { value: '', label: 'All' },
                      { value: 'Parameter', label: 'Revit parameter' },
                      { value: 'BomColumn', label: 'BOM column' },
                      { value: 'Attribute', label: 'PCF attribute' },
                      { value: 'Formula', label: 'Formula' },
                    ]}
                    ariaLabel="Filter mapping source types"
                    help={isNarrowScreen ? undefined : mappingTableTooltips.attributeTypeColumn}
                    inputHeight={compactInputHeight}
                  />
                </Box>
              ),
            },
            {
              key: 'fx',
              width: columnWidths.fx,
              content: (
                <Box ref={formulaColumnRef}>
                  <TableHeaderField
                    label="Value or formula"
                    value={filters.fx}
                    onChange={(value) => handleFilterChange('fx', value)}
                    onSort={() => handleSort('fx')}
                    sortDirection={sortConfig?.key === 'fx' ? sortConfig.direction : null}
                    ariaLabel="Filter direct values or formulas"
                    help={isNarrowScreen ? undefined : mappingTableTooltips.formulaColumn}
                    inputHeight={compactInputHeight}
                  />
                </Box>
              ),
            },
            ...(onRemoveAttribute
              ? [
                  {
                    key: 'rowActions',
                    width: columnWidths.actions,
                    minWidth: columnWidths.actions,
                    align: 'center' as const,
                    content: '',
                  },
                ]
              : []),
          ]}
        />
        <TableBody>
          {sortedAndFilteredAttributes.length === 0 ? (
            <MappingRow>
              <TableCell
                colSpan={tableColumnCount}
                align="center"
                sx={{ color: colors.text.secondary, py: 1.5 }}
              >
                <BodyText secondary>
                  {currentAttributes.length === 0
                    ? 'No PCF attribute mappings are defined for this object type.'
                    : 'No PCF mappings match the current filters.'}
                </BodyText>
                <Box ref={rowActionsRef} sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                  {currentAttributes.length === 0 && onAddAttribute ? (
                    <CompactAddAction onClick={onAddAttribute}>Add PCF mapping</CompactAddAction>
                  ) : hasAnyActiveFilter ? (
                    <Button
                      variant="text"
                      size="small"
                      onClick={clearFilters}
                      sx={{ fontSize: '0.6875rem', py: 0.125 }}
                    >
                      Clear filters
                    </Button>
                  ) : null}
                </Box>
              </TableCell>
            </MappingRow>
          ) : (
            sortedAndFilteredAttributes.map((attr: Attribute) => {
              const ariaProps = getAttributeAriaProps ? getAttributeAriaProps(attr) : undefined;
              const required = isRequired(attr);
              const mapped = isMapped(attr);
              const validationHighlighted = normalizedHighlightedAttributes.includes(
                normalizeAttributeToken(attr.pcfAttributeName)
              );
              return (
                <MappingRow
                  key={attr.order}
                  selected={selectedRows.includes(attr.order)}
                  data-validation-focus={validationHighlighted ? 'true' : undefined}
                  accentColor={
                    validationHighlighted ? theme.palette.warning.main : undefined
                  }
                  {...ariaProps}
                >
                  <TableCell
                    sx={{ width: columnWidths.checkbox, px: 0.25, textAlign: 'center' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={selectedRows.includes(attr.order)}
                      onChange={() => handleSelectRow(attr.order)}
                      aria-label={`Select ${attr.pcfAttributeName}`}
                      inputProps={{ 'aria-label': `Select ${attr.pcfAttributeName}` }}
                      sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: '0.875rem' } }}
                    />
                  </TableCell>
                  {showOrderColumn && (
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        color: colors.text.tertiary,
                        width: columnWidths.order,
                        fontSize: '0.6875rem',
                        textAlign: 'center',
                        px: 0.25,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {attr.order}
                    </TableCell>
                  )}
                  <TableCell sx={{ width: columnWidths.pcfAttribute }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                      {required && (
                        <Tooltip
                          title={renderHelpContent(mappingTableTooltips.requiredAttributeMarker)}
                          placement="top"
                          arrow
                        >
                          <Box
                            component="span"
                            aria-label={`${attr.pcfAttributeName} is required`}
                            sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}
                          >
                            <StarIcon
                              sx={{
                                fontSize: '0.6875rem',
                                color: mapped ? alpha(theme.palette.warning.main, 0.5) : theme.palette.warning.main,
                              }}
                            />
                          </Box>
                        </Tooltip>
                      )}
                      {attr.isCustom === true ? (
                        <TextField
                          size="small"
                          value={attr.pcfAttributeName}
                          onChange={(e) =>
                            onAttributeChange(attr.order, 'pcfAttributeName', e.target.value)
                          }
                          placeholder="Enter PCF attribute key…"
                          autoFocus={attr.pcfAttributeName === 'New Attribute'}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.75rem',
                              height: compactInputHeight,
                              borderRadius: `${borderRadius.sm}px`,
                              backgroundColor: alpha(theme.palette.background.default, 0.2),
                              '& fieldset': {
                                borderRadius: `${borderRadius.sm}px`,
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              py: 0.125,
                              px: theme.spacing(1.75),
                            },
                          }}
                        />
                      ) : (
                        <BodyText
                          secondary
                          sx={{
                            minWidth: 0,
                            px: theme.spacing(1.75),
                          }}
                          noWrap
                          title={attr.pcfAttributeName}
                        >
                          {attr.pcfAttributeName}
                        </BodyText>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ width: columnWidths.revitParam }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <SearchableDropdown
                      options={revitParameterOptions}
                      value={attr.revitParam}
                      onChange={(value) => onAttributeChange(attr.order, 'revitParam', value)}
                      placeholder="Select or enter Revit parameter…"
                      freeSolo
                      ariaLabel={`Revit parameter for ${attr.pcfAttributeName}`}
                      sx={getScopeEditorDropdownSx(theme, compactInputHeight)}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ width: columnWidths.type }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FormControl fullWidth size="small">
                      <Select
                        value={attr.attributeType}
                        onOpen={releaseFocusBeforeOverlay}
                        onChange={(e) =>
                          onAttributeChange(attr.order, 'attributeType', e.target.value as any)
                        }
                        sx={getScopeEditorSelectSx(theme, compactInputHeight)}
                        inputProps={{ 'aria-label': `Mapping source type for ${attr.pcfAttributeName}` }}
                      >
                        <MenuItem value="Parameter">Revit parameter</MenuItem>
                        <MenuItem value="BomColumn">BOM column</MenuItem>
                        <MenuItem value="Attribute">PCF attribute</MenuItem>
                        <MenuItem value="Formula">Formula</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ width: columnWidths.fx }} onClick={(e) => e.stopPropagation()}>
                    {isFormulaMapping(attr) ? (
                      <Stack direction="row" spacing={layoutGap.dense} alignItems="center">
                        <ActionHelpTooltip help={mappingSectionActionTooltips.formulaEditor}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openScriptEditor(attr)}
                            aria-label={`${attr.fx?.trim() ? 'Edit' : 'Add'} formula for ${attr.pcfAttributeName}`}
                            startIcon={<CodeIcon sx={{ fontSize: '0.6875rem' }} />}
                            sx={{
                              minWidth: 48,
                              fontSize: '0.625rem',
                              py: 0.125,
                              px: 0.25,
                              height: compactInputHeight,
                            }}
                          >
                            {attr.fx?.trim() ? 'Edit formula' : 'Add formula'}
                          </Button>
                        </ActionHelpTooltip>
                        
                        {attr.fx?.trim() && (
                          <MappingStatusChip state="mapped" label="Formula set" />
                        )}
                      </Stack>
                    ) : (
                      <TextField
                        size="small"
                        value={attr.fx}
                        onChange={(e) => onAttributeChange(attr.order, 'fx', e.target.value)}
                        fullWidth
                        placeholder="Enter direct value…"
                        sx={getScopeEditorTextFieldSx(theme, compactInputHeight)}
                      />
                    )}
                  </TableCell>
                  {onRemoveAttribute && (
                    <TableCell
                      sx={{ width: columnWidths.actions, px: 0.25, textAlign: 'center' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ActionHelpTooltip help={mappingSectionActionTooltips.deleteAttribute}>
                        <IconButton
                          size="small"
                          aria-label={`Delete ${attr.pcfAttributeName}`}
                          onClick={() => handleRemoveRow(attr)}
                          sx={getScopeEditorActionButtonSx(theme, 'danger')}
                        >
                          <DeleteIcon sx={{ fontSize: '0.6875rem' }} />
                        </IconButton>
                      </ActionHelpTooltip>
                    </TableCell>
                  )}
                </MappingRow>
              );
            })
          )}
          {sortedAndFilteredAttributes.length > 0 && onAddAttribute && (
            <MappingRow
              hover={false}
              sx={{ '&:hover': { backgroundColor: 'transparent' } }}
            >
              <TableCell colSpan={tableColumnCount} sx={{ py: 0.5 }}>
                <Box ref={rowActionsRef} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <ActionHelpTooltip help={mappingSectionActionTooltips.addAttribute}>
                    <CompactAddAction onClick={onAddAttribute}>Add PCF mapping</CompactAddAction>
                  </ActionHelpTooltip>
                </Box>
              </TableCell>
            </MappingRow>
          )}
        </TableBody>
      </ScopeEditorTableVariant>

      <PythonScriptEditorDialog
        open={isScriptEditorOpen}
        attributeName={editingAttribute?.pcfAttributeName || ''}
        objectType={selectedObjectType}
        initialScript={editingAttribute?.fx || ''}
        onClose={closeScriptEditor}
        onSave={saveScript}
      />
    </Box>
  );
};

export default React.memo(MappingTable);
